import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  buttonVisualStateToCssDeclarations,
  buttonVisualStateToReactStyle,
  normalizeButtonDesignStyle,
} from '../../../lib/buttonDesignStyles';
import {
  getSocialContainerClasses,
  getSocialItemClasses,
  resolveSocialVisualVariant,
  resolveSocialAlignX,
  resolveSocialAlignY,
  resolveSocialIconSize,
  resolveSocialOrientation,
  resolveSocialSizingMode,
  SOCIAL_ICON_SIZE_PX,
} from '../../../lib/elementStyleTokens';
import { renderSocialIconByLabel } from '../../../lib/socialIcons';
import { cn } from '../../../lib/utils';
import { resolveFooterContent } from '../../../lib/footerContent';
import Footer from '../Footer';
import {
  resolveStepTrackerActiveStep,
  resolveStepTrackerContent,
} from '../../../lib/progressContent';
import ProgressIndicator from '../../ProgressIndicator';
import RequestFormSurface from '../../flow/RequestFormSurface';
import type { EnquiryFormData } from '../../../types';

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type SelectionOverlayRenderer = (block: CanvasSectionBlock) => React.ReactNode;

type BaseRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: SelectionOverlayRenderer;
};

export function SpacerBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  fontLibrary,
  isAdmin,
}: BaseRendererProps & {
  fontLibrary: Record<string, string>;
  isAdmin: boolean;
}) {
  const spacerDesignStyle = normalizeButtonDesignStyle(block.style);
  const spacerStyle = buttonVisualStateToReactStyle(spacerDesignStyle, fontLibrary);
  const spacerRawStyle =
    block.style && typeof block.style === 'object' && !Array.isArray(block.style)
      ? (block.style as Record<string, unknown>)
      : {};
  const spacerAtmosphereStyle: React.CSSProperties = {
    backgroundImage:
      typeof spacerRawStyle.backgroundImage === 'string' ? spacerRawStyle.backgroundImage : undefined,
    filter: typeof spacerRawStyle.filter === 'string' ? spacerRawStyle.filter : undefined,
    mixBlendMode:
      typeof spacerRawStyle.mixBlendMode === 'string'
        ? (spacerRawStyle.mixBlendMode as React.CSSProperties['mixBlendMode'])
        : undefined,
  };
  const spacerWrapperProps = isAdmin
    ? blockWrapperProps
    : {
        ...blockWrapperProps,
        className: String(blockWrapperProps.className ?? '').replace(
          'pointer-events-auto',
          'pointer-events-none'
        ),
      };

  return (
    <div key={block.id} {...spacerWrapperProps}>
      <div
        {...(selectionTarget === 'content' ? selectableBlockHitProps : {})}
        className="h-full w-full"
        style={{ ...spacerStyle, ...spacerAtmosphereStyle }}
      >
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}

export function RequestFormBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  logicNodeTag,
  sectionId,
  enquiryEmailTo,
  onEnquirySubmit,
  isAdmin,
  isInteractPreview,
}: BaseRendererProps & {
  logicNodeTag: React.ReactNode;
  sectionId?: string;
  enquiryEmailTo?: string;
  onEnquirySubmit?: (formData: EnquiryFormData) => void;
  isAdmin: boolean;
  isInteractPreview: boolean;
}) {
  return (
    <div key={block.id} {...blockWrapperProps}>
      {logicNodeTag}
      <div {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})} className="h-full w-full">
        <RequestFormSurface
          block={block}
          sectionId={sectionId}
          enquiryEmailTo={enquiryEmailTo}
          onEnquirySubmit={onEnquirySubmit}
          isAdmin={isAdmin}
          isInteractPreview={isInteractPreview}
          selectionOverlay={renderSelectionOverlay(block)}
        />
      </div>
    </div>
  );
}

export function FooterBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
}: BaseRendererProps) {
  const footerContent = resolveFooterContent(
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : undefined
  );
  return (
    <div key={block.id} {...blockWrapperProps}>
      <div {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})} className="h-full w-full">
        <Footer settings={footerContent} />
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}

export function ProgressBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  flowState,
  flows,
  isAdmin,
  isInteractPreview,
  isImmersivePreview,
}: BaseRendererProps & {
  flowState: unknown;
  flows: unknown[];
  isAdmin: boolean;
  isInteractPreview: boolean;
  isImmersivePreview: boolean;
}) {
  const stepTracker = resolveStepTrackerContent(
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : undefined,
    block.id
  );
  const activeStepTracker = resolveStepTrackerActiveStep(
    flowState as never,
    flows as never,
    stepTracker.trackerId
  );
  const renderInPlacePreview = isAdmin && !isInteractPreview && !isImmersivePreview;
  return (
    <div key={block.id} {...blockWrapperProps}>
      <div {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})} className="h-full w-full">
        <ProgressIndicator
          steps={stepTracker.steps}
          placement={renderInPlacePreview ? 'static' : 'fixed'}
          activeStepId={activeStepTracker.stepId ?? stepTracker.defaultStepId}
          statusMode={activeStepTracker.hasAction ? stepTracker.statusMode : 'current'}
          stickySectionIds={stepTracker.stickySectionIds}
          forceVisible={renderInPlacePreview}
        />
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}

export function VideoBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
}: BaseRendererProps) {
  const src = String((block.content?.src as string) ?? '').trim();
  const title = String((block.content?.title as string) ?? 'Video block');
  const videoFitMode = block.content?.videoFitMode === 'contain' ? 'contain' : 'fill';
  const rawAspectRatio = String(block.content?.videoAspectRatio ?? 'free');
  const videoAspectRatio =
    rawAspectRatio === '16:9' ||
    rawAspectRatio === '4:3' ||
    rawAspectRatio === '1:1' ||
    rawAspectRatio === '9:16'
      ? rawAspectRatio
      : 'free';
  const aspectRatioValue =
    videoAspectRatio === '16:9'
      ? 16 / 9
      : videoAspectRatio === '4:3'
        ? 4 / 3
        : videoAspectRatio === '1:1'
          ? 1
          : videoAspectRatio === '9:16'
            ? 9 / 16
            : null;
  const useContainedAspectPlayer = videoFitMode === 'contain' && aspectRatioValue !== null;
  const videoPlayerStyle: React.CSSProperties | undefined = useContainedAspectPlayer
    ? {
        aspectRatio: videoAspectRatio.replace(':', ' / '),
        width: `min(100%, calc(100cqh * ${aspectRatioValue}))`,
        height: `min(100%, calc(100cqw / ${aspectRatioValue}))`,
      }
    : undefined;

  return (
    <div key={block.id} {...blockWrapperProps}>
      <div
        {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})}
        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-stone-300 bg-stone-50 text-xs text-stone-500"
        style={useContainedAspectPlayer ? { containerType: 'size' } : undefined}
      >
        {src ? (
          useContainedAspectPlayer ? (
            <div className="max-h-full max-w-full overflow-hidden" style={videoPlayerStyle}>
              <iframe
                src={src}
                title={title}
                className="h-full w-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <iframe
              src={src}
              title={title}
              className="h-full w-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center px-3 text-center">
            Video block placeholder
          </div>
        )}
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}

export function SocialBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  fontLibrary,
}: BaseRendererProps & {
  fontLibrary: Record<string, string>;
}) {
  const linksRaw = Array.isArray(block.content?.links)
    ? (block.content?.links as Array<Record<string, unknown>>)
    : [];
  const links = linksRaw.slice(0, 5);
  const socialVariant = resolveSocialVisualVariant(block.content?.variant);
  const socialSizingMode = resolveSocialSizingMode(block.content?.socialSizingMode);
  const socialOrientation = resolveSocialOrientation(block.content?.socialOrientation, socialVariant);
  const socialAlignX = resolveSocialAlignX(block.content?.socialAlignX);
  const socialAlignY = resolveSocialAlignY(block.content?.socialAlignY);
  const socialIconSize = resolveSocialIconSize(block.content?.socialIconSize);
  const socialIconSizePx = SOCIAL_ICON_SIZE_PX[socialIconSize];
  const socialContainerClass = getSocialContainerClasses(socialVariant);
  const socialLinkClass = getSocialItemClasses(socialVariant);
  const socialDesignStyle = normalizeButtonDesignStyle(block.style);
  const socialRegularStyle = buttonVisualStateToReactStyle(socialDesignStyle, fontLibrary);
  const socialHoverCss = buttonVisualStateToCssDeclarations(socialDesignStyle.hover, fontLibrary);
  const socialDomId = `builder-social-${block.id}`;
  const frameAlignItems =
    socialAlignY === 'top' ? 'start' : socialAlignY === 'bottom' ? 'end' : 'center';
  const frameJustifyItems =
    socialAlignX === 'left' ? 'start' : socialAlignX === 'right' ? 'end' : 'center';
  const groupJustifyContent =
    socialSizingMode === 'stretch'
      ? links.length > 1
        ? 'space-between'
        : socialAlignX === 'left'
          ? 'flex-start'
          : socialAlignX === 'right'
            ? 'flex-end'
            : 'center'
      : socialAlignX === 'left'
        ? 'flex-start'
        : socialAlignX === 'right'
          ? 'flex-end'
          : 'center';
  const groupAlignItems =
    socialOrientation === 'row'
      ? socialAlignY === 'top'
        ? 'flex-start'
        : socialAlignY === 'bottom'
          ? 'flex-end'
          : 'center'
      : socialAlignX === 'left'
        ? 'flex-start'
        : socialAlignX === 'right'
          ? 'flex-end'
          : 'center';
  const socialGroupStyle: React.CSSProperties = {
    width: socialSizingMode === 'stretch' ? '100%' : 'fit-content',
    height: socialSizingMode === 'stretch' && socialOrientation === 'stack' ? '100%' : 'fit-content',
    justifyContent: groupJustifyContent,
    alignItems: groupAlignItems,
  };
  const socialItemStyle: React.CSSProperties = {
    ...socialRegularStyle,
    width: socialIconSizePx,
    height: socialIconSizePx,
    minWidth: socialIconSizePx,
    minHeight: socialIconSizePx,
    fontSize: socialIconSizePx,
    flex: '0 0 auto',
  };

  return (
    <div key={block.id} {...blockWrapperProps}>
      {socialHoverCss ? (
        <style>{`[data-builder-social-id="${socialDomId}"]:hover { ${socialHoverCss} }`}</style>
      ) : null}
      <div
        {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})}
        className="relative grid h-full w-full"
        style={{ alignItems: frameAlignItems, justifyItems: frameJustifyItems }}
      >
        <div
          className={cn(
            socialContainerClass,
            socialOrientation === 'stack' ? 'flex-col' : 'flex-row',
            socialSizingMode === 'stretch' ? 'w-full' : 'w-fit',
            socialSizingMode === 'stretch' && socialOrientation === 'stack' ? 'h-full' : 'h-fit'
          )}
          style={socialGroupStyle}
        >
          {links.length > 0 ? (
            links.map((link, index) => {
              const href = String(link.href ?? '#');
              const label = String(link.label ?? `Link ${index + 1}`);
              return (
                <a
                  key={`${block.id}-social-${index}`}
                  href={href || '#'}
                  data-builder-social-id={socialDomId}
                  className={socialLinkClass}
                  style={socialItemStyle}
                  aria-label={label}
                  title={label}
                >
                  {renderSocialIconByLabel(label, href)}
                </a>
              );
            })
          ) : (
            <span className="text-xs text-stone-500">Social links</span>
          )}
        </div>
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}

export function UnknownBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
}: BaseRendererProps) {
  return (
    <div key={block.id} {...blockWrapperProps}>
      <div
        {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})}
        className="relative h-full w-full rounded-lg border border-dashed border-stone-200 bg-transparent"
      >
        {renderSelectionOverlay(block)}
      </div>
    </div>
  );
}
